import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

import { Badge } from '@/shared/components/ui/Badge';
import { ButtonArrow } from '@/shared/components/ui/ButtonArrow';
import { useI18n } from '@/shared/contexts/I18nProvider';

import { infrastructureCardIcons } from '../HomeData';

const operationFlowPulseOffsets = [0, 0.48] as const;

const operationFlowRoutes = [
  {
    key: 'private-data-paths',
    linePath: 'M600 36 C520 92 348 148 184 220',
    path: 'M600 0 L600 36 C520 92 348 148 184 220',
    mobileLinePath: 'M195 36 C156 112 118 224 96 330',
    mobilePath: 'M195 0 L195 36 C156 112 118 224 96 330',
    endX: 184,
    endY: 220,
    mobileEndX: 96,
    mobileEndY: 330,
    duration: 7.4,
    delay: 0
  },
  {
    key: 'controlled-execution',
    linePath: 'M600 36 C680 92 852 148 1016 220',
    path: 'M600 0 L600 36 C680 92 852 148 1016 220',
    mobileLinePath: 'M195 36 C234 112 272 224 294 330',
    mobilePath: 'M195 0 L195 36 C234 112 272 224 294 330',
    endX: 1016,
    endY: 220,
    mobileEndX: 294,
    mobileEndY: 330,
    duration: 7,
    delay: 0.55
  },
  {
    key: 'audit-ready-activity',
    linePath: 'M600 36 C500 176 404 364 280 545',
    path: 'M600 0 L600 36 C500 176 404 364 280 545',
    mobileLinePath: 'M195 36 C154 208 116 408 96 532',
    mobilePath: 'M195 0 L195 36 C154 208 116 408 96 532',
    endX: 280,
    endY: 545,
    mobileEndX: 96,
    mobileEndY: 532,
    duration: 7,
    delay: 1.1
  },
  {
    key: 'policy-controls',
    linePath: 'M600 36 C600 184 600 378 600 565',
    path: 'M600 0 L600 36 C600 184 600 378 600 565',
    mobileLinePath: 'M195 36 C195 250 195 456 195 660',
    mobilePath: 'M195 0 L195 36 C195 250 195 456 195 660',
    endX: 600,
    endY: 565,
    mobileEndX: 195,
    mobileEndY: 660,
    duration: 7.2,
    delay: 1.38
  },
  {
    key: 'operational-visibility',
    linePath: 'M600 36 C700 176 796 364 920 545',
    path: 'M600 0 L600 36 C700 176 796 364 920 545',
    mobileLinePath: 'M195 36 C236 208 274 408 294 532',
    mobilePath: 'M195 0 L195 36 C236 208 274 408 294 532',
    endX: 920,
    endY: 545,
    mobileEndX: 294,
    mobileEndY: 532,
    duration: 7.4,
    delay: 1.65
  }
] as const;

const operationCardPositions = [
  'left-4 top-[19.25rem] w-[calc(50%-1.25rem)] lg:left-[6%] lg:top-[31%] lg:w-56',
  'right-4 top-[19.25rem] w-[calc(50%-1.25rem)] lg:right-[6%] lg:top-[31%] lg:w-56',
  'left-4 top-[31.75rem] w-[calc(50%-1.25rem)] lg:left-[14%] lg:top-auto lg:bottom-[9%] lg:w-56',
  'left-1/2 top-[39rem] w-[calc(100%-2rem)] -translate-x-1/2 lg:top-auto lg:bottom-[6%] lg:w-56',
  'right-4 top-[31.75rem] w-[calc(50%-1.25rem)] lg:right-[14%] lg:top-auto lg:bottom-[9%] lg:w-56'
] as const;

const mobileVerticalFlowPath = 'M195 0 L195 690';

const renderDesktopOperationFlow = () => (
  <>
    <path
      d="M600 0 L600 36"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="1.5"
      strokeOpacity="0.38"
      vectorEffect="non-scaling-stroke"
    />

    {operationFlowRoutes.map(route => {
      return (
        <path
          key={route.key}
          d={route.linePath}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeOpacity="0.22"
          vectorEffect="non-scaling-stroke"
        />
      );
    })}

    {operationFlowRoutes.flatMap(route => {
      return operationFlowPulseOffsets.flatMap((offset, index) => {
        const begin = route.delay + route.duration * offset;
        const arrival = begin + route.duration;

        return [
          <g
            key={`${route.key}-particle-${offset}`}
            className="onprem-operation-flow-particle"
            opacity="0"
          >
            <circle
              r={index === 0 ? '9' : '7'}
              fill="currentColor"
              opacity="0.13"
            />
            <circle r={index === 0 ? '3.4' : '2.8'} fill="currentColor" />
            <animateMotion
              path={route.path}
              dur={`${route.duration}s`}
              begin={`${begin}s`}
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values="0;1;1;0"
              keyTimes="0;0.08;0.88;1"
              dur={`${route.duration}s`}
              begin={`${begin}s`}
              repeatCount="indefinite"
            />
          </g>,
          <g
            key={`${route.key}-droplet-${offset}`}
            className="onprem-operation-flow-droplet"
            transform={`translate(${route.endX} ${route.endY})`}
          >
            <circle fill="currentColor" opacity="0">
              <animate
                attributeName="r"
                values="0;18;28;0"
                keyTimes="0;0.06;0.18;1"
                dur={`${route.duration}s`}
                begin={`${arrival}s`}
                repeatCount="indefinite"
              />
              <animate
                attributeName="opacity"
                values="0;0.28;0.08;0"
                keyTimes="0;0.06;0.18;1"
                dur={`${route.duration}s`}
                begin={`${arrival}s`}
                repeatCount="indefinite"
              />
            </circle>
            <circle fill="currentColor" opacity="0">
              <animate
                attributeName="r"
                values="0;5;0"
                keyTimes="0;0.08;1"
                dur={`${route.duration}s`}
                begin={`${arrival}s`}
                repeatCount="indefinite"
              />
              <animate
                attributeName="opacity"
                values="0;0.72;0"
                keyTimes="0;0.08;1"
                dur={`${route.duration}s`}
                begin={`${arrival}s`}
                repeatCount="indefinite"
              />
            </circle>
          </g>
        ];
      });
    })}
  </>
);

const renderMobileOperationFlow = () => (
  <>
    <path
      d={mobileVerticalFlowPath}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="1.4"
      strokeOpacity="0.28"
      vectorEffect="non-scaling-stroke"
    />

    {[0, 0.24, 0.48, 0.72].map((offset, index) => {
      const duration = 8.4;
      const begin = duration * offset;

      return (
        <g
          key={`mobile-vertical-particle-${offset}`}
          className="onprem-operation-flow-particle"
          opacity="0"
        >
          <circle
            r={index % 2 === 0 ? '8' : '6.5'}
            fill="currentColor"
            opacity="0.13"
          />
          <circle r={index % 2 === 0 ? '3' : '2.5'} fill="currentColor" />
          <animateMotion
            path={mobileVerticalFlowPath}
            dur={`${duration}s`}
            begin={`${begin}s`}
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0;1;1;0"
            keyTimes="0;0.08;0.9;1"
            dur={`${duration}s`}
            begin={`${begin}s`}
            repeatCount="indefinite"
          />
        </g>
      );
    })}
  </>
);

const OperationsBackgroundFlow = () => (
  <div
    aria-hidden="true"
    className="pointer-events-none absolute inset-0 z-0 overflow-hidden text-accent-primary"
  >
    <svg
      viewBox="0 0 1200 620"
      className="absolute left-1/2 top-0 hidden h-full w-[1200px] -translate-x-1/2 lg:block"
      preserveAspectRatio="xMidYMin slice"
    >
      {renderDesktopOperationFlow()}
    </svg>

    <svg
      viewBox="0 0 390 720"
      className="absolute left-1/2 top-0 h-full w-full max-w-[520px] -translate-x-1/2 lg:hidden"
      preserveAspectRatio="xMidYMin slice"
    >
      {renderMobileOperationFlow()}
    </svg>
  </div>
);

export const Security = () => {
  const { content, localizedPath } = useI18n();
  const infrastructureCards = content.home.security.infrastructureCards.map(
    (card, index) => ({
      ...card,
      icon: infrastructureCardIcons[index] ?? infrastructureCardIcons[0]
    })
  );

  return (
    <section
      id="security"
      className="relative scroll-mt-24 overflow-hidden border-t border-border-muted bg-background-primary"
    >
      <div className="relative mx-auto h-[700px] max-w-7xl px-4 sm:px-6 lg:h-[660px] lg:px-8">
        <OperationsBackgroundFlow />

        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-0 z-0 h-[28rem] w-[min(46rem,88vw)] -translate-x-1/2 bg-[radial-gradient(ellipse_at_top,rgba(132,202,255,0.1),transparent_68%)]"
        />

        <div className="absolute left-1/2 top-24 z-20 w-[min(34rem,calc(100%-2rem))] -translate-x-1/2 text-center lg:top-1/2 lg:-translate-y-1/2">
          <div className="rounded-lg border border-accent-primary/30 bg-background-card/86 p-6 shadow-2xl shadow-shadow backdrop-blur sm:p-8">
            <Badge variant="secondary" className="mb-4">
              {content.home.security.badge}
            </Badge>

            <h2 className="text-4xl font-light tracking-tight sm:text-5xl">
              {content.home.security.title}
            </h2>

            <Link
              to={localizedPath('/contact')}
              className="mt-7 inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-md bg-text-primary px-8 font-mono text-sm font-medium text-text-inverse transition-colors hover:bg-text-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2"
            >
              {content.home.security.bookDemo}
              <ButtonArrow className="h-5 w-5" />
            </Link>
          </div>
        </div>

        <div className="pointer-events-none absolute inset-0 z-10 hidden lg:block">
          {infrastructureCards.map((card, index) => {
            const route = operationFlowRoutes[index] ?? operationFlowRoutes[0];
            const dropletDelay = route.delay + route.duration;

            return (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.45, delay: index * 0.06 }}
                className={[
                  'group pointer-events-auto absolute overflow-hidden rounded-md border border-border-primary bg-background-card/76 px-4 py-3 text-center shadow-sm backdrop-blur transition-colors duration-300 hover:border-accent-primary/45',
                  operationCardPositions[index] ?? ''
                ].join(' ')}
              >
                <div
                  aria-hidden="true"
                  className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent-primary/45 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                />

                <span
                  aria-hidden="true"
                  className="onprem-operation-card-droplet absolute left-1/2 top-1/2 h-7 w-7 rounded-full border border-accent-primary/35 bg-accent-primary/10"
                  style={{
                    animationDelay: `${dropletDelay}s`,
                    animationDuration: `${route.duration}s`
                  }}
                />

                <h3 className="relative font-mono text-xs uppercase tracking-[0.14em] text-text-secondary">
                  {card.title}
                </h3>
                {route.key === 'policy-controls' && (
                  <span className="relative mt-1 block font-mono text-[10px] uppercase tracking-[0.16em] text-accent-primary">
                    {content.common.soc2Compliant}
                  </span>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
